import { useEffect, useMemo, useState } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import LanguageSwitcher from "@/components/feature/LanguageSwitcher";
import { clearAdminToken } from "@/admin/auth";
import { fetchAdminProfile } from "@/admin/api";
import type { AdminProfile } from "@/admin/types";
import { useSiteSettings } from "@/site/SiteSettingsContext";
import { useDarkMode } from "@/site/DarkModeContext";

const navItems = [
  { key: "adminDashboard", path: "/admin", icon: "ri-dashboard-2-line" },
  { key: "adminSkills", path: "/admin/skills", icon: "ri-star-smile-line" },
  { key: "adminCategories", path: "/admin/categories", icon: "ri-stack-line" },
  { key: "adminAgeGroups", path: "/admin/age-groups", icon: "ri-time-line" },
  { key: "adminCrm", path: "/admin/crm", icon: "ri-user-heart-line" },
  { key: "adminProducts", path: "/admin/products", icon: "ri-shopping-bag-3-line" },
  { key: "adminOrders", path: "/admin/orders", icon: "ri-file-list-3-line" },
  { key: "adminMappings", path: "/admin/mappings", icon: "ri-link" },
  { key: "adminSettings", path: "/admin/settings", icon: "ri-settings-3-line" },
];

export default function AdminLayout() {
  const { t, i18n } = useTranslation("common");
  const navigate = useNavigate();
  const location = useLocation();
  const [profile, setProfile] = useState<AdminProfile | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const isRtl = i18n.dir() === "rtl";
  const { settings } = useSiteSettings();
  const { isDarkMode, toggleDarkMode } = useDarkMode();
  const logoUrl = settings?.logoUrl?.trim();

  useEffect(() => {
    let isMounted = true;
    setIsLoading(true);
    fetchAdminProfile()
      .then((data) => {
        if (!isMounted) return;
        setProfile(data);
      })
      .catch(() => {
        if (!isMounted) return;
        clearAdminToken();
        navigate("/admin/login");
      })
      .finally(() => {
        if (!isMounted) return;
        setIsLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [navigate]);

  // Auto close sidebar on mobile when navigating
  useEffect(() => {
    setIsSidebarOpen(false);
  }, [location.pathname]);

  const filteredNavItems = useMemo(() => {
    if (!profile) return navItems;
    if (profile.role === "Content Editor") {
      return navItems.filter((item) => ["adminDashboard", "adminAgeGroups", "adminCategories", "adminSkills"].includes(item.key));
    }
    return navItems;
  }, [profile]);

  const pageTitleKey = useMemo(() => {
    const match = filteredNavItems.find((item) => item.path === location.pathname);
    if (match) return match.key;
    const fallback = filteredNavItems.find((item) => location.pathname.startsWith(item.path));
    return fallback?.key || "adminDashboard";
  }, [filteredNavItems, location.pathname]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FFFBF7] dark:bg-zinc-950">
        <div className="text-center text-gray-500 dark:text-zinc-400">{t("loading")}</div>
      </div>
    );
  }

  if (!profile) {
    return null;
  }

  return (
    <div className="h-screen bg-[#F6F3EF] dark:bg-zinc-950 text-gray-800 dark:text-zinc-200 relative overflow-hidden transition-colors duration-300 flex flex-col">
      <div className="absolute inset-0 pointer-events-none bg-[radial-gradient(1200px_circle_at_10%_10%,rgba(232,146,124,0.18),transparent_60%),radial-gradient(900px_circle_at_90%_0%,rgba(42,157,143,0.15),transparent_55%),radial-gradient(700px_circle_at_20%_90%,rgba(233,196,106,0.15),transparent_50%)] dark:opacity-30" />
      
      {/* Mobile Sidebar Backdrop Overlay */}
      {isSidebarOpen && (
        <div
          onClick={() => setIsSidebarOpen(false)}
          className="fixed inset-0 bg-black/40 backdrop-blur-sm z-40 lg:hidden transition-all duration-300"
        />
      )}

      <div className="relative flex lg:gap-6 flex-1 h-full overflow-hidden p-4">
        <aside
          className={`w-80 h-[calc(100vh-2rem)] lg:h-full bg-white/95 lg:bg-white/85 dark:bg-zinc-900/95 lg:dark:bg-zinc-900/85 backdrop-blur-xl border border-white/70 dark:border-zinc-800/80 shadow-[0_25px_70px_-30px_rgba(15,23,42,0.3)] dark:shadow-[0_25px_70px_-30px_rgba(0,0,0,0.5)] rounded-3xl px-5 py-7 flex flex-col justify-between shrink-0 transition-all duration-300 z-50
            fixed top-4 bottom-4 lg:static
            ${isRtl 
              ? `${isSidebarOpen ? "right-4" : "-right-96"} lg:right-auto text-right` 
              : `${isSidebarOpen ? "left-4" : "-left-96"} lg:left-auto text-left`
            }
          `}
        >
          <div className="flex flex-col flex-1 overflow-hidden">
            <div className="mb-6 pb-5 border-b border-gray-100 dark:border-zinc-800 shrink-0">
              <div className="flex items-center gap-3">
                <a
                  href="/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="transition-transform hover:scale-105 shrink-0"
                  title={t("adminVisitWebsite")}
                >
                  {logoUrl ? (
                    <img
                      src={logoUrl}
                      alt=""
                      className="w-11 h-11 rounded-2xl object-contain border border-gray-100 dark:border-zinc-800 bg-white dark:bg-zinc-900 shadow-sm"
                    />
                  ) : (
                    <span className="w-11 h-11 rounded-2xl bg-gradient-to-br from-primary-400 to-primary-500 text-white flex items-center justify-center text-xl shadow-[0_10px_25px_-12px_rgba(232,146,124,0.9)]">
                      <i className="ri-shield-star-line" />
                    </span>
                  )}
                </a>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center justify-between gap-1 w-full">
                    <h1 className="text-base font-bold text-navy-400 dark:text-zinc-100 truncate">{t("adminTitle")}</h1>
                    <div className="flex items-center gap-1 shrink-0">
                      {/* Mobile Close Button */}
                      <button
                        onClick={() => setIsSidebarOpen(false)}
                        className="lg:hidden w-6 h-6 rounded-lg bg-gray-50 dark:bg-zinc-800 border border-gray-100 dark:border-zinc-700 flex items-center justify-center text-gray-400 hover:text-red-500 transition-all"
                        title="Close"
                      >
                        <i className="ri-close-line text-base" />
                      </button>
                      <a
                        href="/"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-6 h-6 rounded-lg bg-gray-50 dark:bg-zinc-800 border border-gray-100 dark:border-zinc-700 flex items-center justify-center text-gray-400 hover:text-primary-500 hover:bg-primary-50 hover:border-primary-100 dark:hover:bg-zinc-700 transition-all"
                        title={t("adminVisitWebsite")}
                      >
                        <i className="ri-external-link-line text-sm" />
                      </a>
                    </div>
                  </div>
                  <p className="text-xs text-gray-400 dark:text-zinc-500 truncate mt-0.5">{profile.email}</p>
                </div>
              </div>
            </div>
            <nav className={`space-y-1.5 flex-1 overflow-y-auto overflow-x-hidden ${isRtl ? "pl-1 -ml-1" : "pr-1 -mr-1"}`}>
              {filteredNavItems.map((item) => {
                const isActive = item.path === "/admin"
                  ? location.pathname === "/admin"
                  : location.pathname.startsWith(item.path);
                return (
                  <button
                    key={item.key}
                    onClick={() => navigate(item.path)}
                    dir={isRtl ? "rtl" : "ltr"}
                    className={`w-full px-4 py-3 rounded-2xl text-base font-semibold flex items-center gap-3 justify-start transition-all ${
                      isActive
                        ? "bg-gradient-to-r from-primary-400 to-primary-500 text-white shadow-[0_15px_30px_-18px_rgba(232,146,124,0.95)] scale-[1.02]"
                        : "text-gray-700 dark:text-zinc-300 hover:bg-primary-50/70 dark:hover:bg-zinc-800/50 hover:text-primary-500 dark:hover:text-teal-400"
                    }`}
                  >
                    <span
                      className={`w-9 h-9 rounded-xl flex items-center justify-center text-lg shrink-0 ${
                        isActive
                          ? "bg-white/25 text-white"
                          : "bg-primary-50 dark:bg-zinc-800 text-primary-500 dark:text-teal-400"
                      }`}
                    >
                      <i className={item.icon} />
                    </span>
                    <span className="truncate">{t(item.key)}</span>
                  </button>
                );
              })}
            </nav>
          </div>
          
          <div className="pt-4 mt-4 border-t border-gray-100 dark:border-zinc-800 flex flex-col gap-3 shrink-0">
            <div className="flex items-center justify-between gap-2">
              <LanguageSwitcher alignUp={true} />
              <button
                onClick={toggleDarkMode}
                className="flex items-center justify-center w-10 h-10 rounded-xl bg-gray-50 dark:bg-zinc-800 text-gray-700 dark:text-zinc-300 hover:bg-gray-100 dark:hover:bg-zinc-700/80 transition-all shadow-sm border border-gray-100 dark:border-zinc-800"
                title={t("toggleTheme")}
              >
                {isDarkMode ? (
                  <i className="ri-sun-fill text-lg text-amber-500" />
                ) : (
                  <i className="ri-moon-fill text-lg text-navy-400 dark:text-zinc-400" />
                )}
              </button>
            </div>
            <button
              onClick={() => {
                clearAdminToken();
                navigate("/admin/login");
              }}
              className="w-full flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-bold rounded-xl bg-red-50 dark:bg-red-950/20 text-red-500 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-950/30 transition-all border border-red-100 dark:border-red-950/20"
            >
              <i className="ri-logout-box-line text-base" />
              <span>{t("adminLogout")}</span>
            </button>
          </div>
        </aside>
        <div className="flex-1 flex flex-col h-full overflow-hidden">
          <header className="bg-white/70 dark:bg-zinc-900/70 backdrop-blur-xl border border-white/60 dark:border-zinc-800 rounded-3xl px-6 py-4 shadow-[0_25px_70px_-50px_rgba(15,23,42,0.4)] dark:shadow-[0_25px_70px_-50px_rgba(0,0,0,0.6)] shrink-0 z-35">
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                {/* Mobile Menu Toggle button */}
                <button
                  onClick={() => setIsSidebarOpen(true)}
                  className="lg:hidden w-10 h-10 rounded-xl bg-gray-50 dark:bg-zinc-850 border border-gray-100 dark:border-zinc-750 flex items-center justify-center text-gray-700 dark:text-zinc-300 hover:bg-gray-100 dark:hover:bg-zinc-800 transition-all shrink-0"
                  title="Menu"
                >
                  <i className="ri-menu-line text-lg" />
                </button>
                <div>
                  <h2 className="text-lg md:text-xl font-bold text-navy-400 dark:text-zinc-100">{t(pageTitleKey)}</h2>
                  <p className="text-xs text-gray-400 dark:text-zinc-500">{t("adminWelcome", { name: profile.name })}</p>
                </div>
              </div>
            </div>
          </header>
          <main className={`flex-1 overflow-y-auto mt-4 pb-10 ${isRtl ? "pl-1" : "pr-1"}`}>
            <div className="admin-panel">
              <Outlet context={{ profile }} />
            </div>
          </main>
        </div>
      </div>
    </div>
  );
}
